import clsx from 'clsx';
import React from 'react';
import { Controller } from 'react-hook-form';
import Select, { components, DropdownIndicatorProps, Props } from 'react-select';

import { FormError } from '../FormElements/FormElements';

import styles from './Select.module.scss';

export const customStyles = {
  // default styles --> https://github.com/JedWatson/react-select/tree/master/packages/react-select/src/components
  control: () => ({
    display: 'flex',
    border: '1px solid #628fb9',
    borderRadius: '8px',
    color: '#628fb9',
    padding: '10px',
    backgroundColor: '#fff',
  }),
  valueContainer: (provided: any) => ({
    ...provided,
    padding: '0 12px',
  }),
  multiValue: () => ({
    display: 'flex',
    padding: '8px 12px',
    background: '#4b5052',
    margin: '2px',
    borderRadius: '8px',
  }),
  multiValueLabel: () => ({
    color: '#fff',
    fontSize: '14px',
    lineHeight: '16px',
    fontWeight: 400,
  }),
  multiValueRemove: () => ({
    color: '#fff',
    marginLeft: '8px',
    lineHeight: 1,
    cursor: 'pointer',
    ':hover': {
      opacity: '.7',
    },
  }),
  menu: (provided: any) => ({
    ...provided,
    boxShadow: '0px 15px 30px -2px rgb(0 0 0 / 10%)',
    borderRadius: '8px',
    padding: '0 0',
  }),
  option: (provided: any) => ({
    ...provided,
    padding: '8px 30px',
    margin: '3px 0',
    cursor: 'pointer',
    fontWeight: 400,
    lineHeight: '22px',
    letterSpacing: '0.73px',
    borderRadius: '6px',
  }),
  dropdownIndicator: (provided: any) => ({
    ...provided,
    cursor: 'pointer',
    color: '#4b5052',
  }),
  clearIndicator: (provided: any) => ({
    ...provided,
    cursor: 'pointer',
  }),
  placeholder: (provided: any) => ({
    ...provided,
    color: '#4b5052',
  }),
};

type OptionProps = {
  value: string | number;
  label: string;
  icon?: React.ReactNode;
};

type ReactSelectProps = {
  options: OptionProps[];
  control?: any;
  name: string;
  value?: string | number | string[] | number[];
  selectProps?: Props;
  rules?: Object;
  className?: string;
  theme?: 'white' | 'blue';
  placeholder?: string;
  label?: string;
  id?: any;
  readOnly?: boolean;
  customOnChange?: (value: string | number | (string | number)[]) => void;
  testId?: string;
};

const makeDropdownIndicator = (testId: string) =>
  function ({ children, innerProps, ...props }: DropdownIndicatorProps) {
    return (
      <components.DropdownIndicator
        {...props}
        innerProps={{ ...(innerProps as any), 'data-testid': testId }}
      >
        {children}
      </components.DropdownIndicator>
    );
  };

const formatOptionLabel = ({ label, icon }) => (
  <div
    className={styles.option}
    data-testid={`select-option-${label}`}
  >
    <div className={styles.label}>{label}</div>
    {icon && <div className={styles.icon}>{icon}</div>}
  </div>
);

export const CustomSelect: React.FC<ReactSelectProps> = ({
  options,
  control,
  name,
  value,
  selectProps,
  rules,
  label,
  className,
  theme = 'white',
  placeholder,
  id,
  readOnly,
  customOnChange,
  testId,
}) => {
  const valueData = React.useMemo(
    () =>
      selectProps?.isMulti
        ? options?.filter(option => (value as [])?.some(v => v === option.value))
        : options?.find(option => value === option.value),
    [value, options, selectProps?.isMulti],
  );

  if (selectProps?.isMulti && (valueData as any[])?.length === 0 && (value as any[])?.length > 0) return null;

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={value}
      rules={rules}
      render={({ fieldState: { error }, field: { onChange, ref } }) => (
        <div
          className={clsx(styles.select, className, styles[theme], {
            [styles.error]: error,
            [styles.withLabel]: label,
          })}
          id={id}
        >
          {label && <div className={styles.label}>{label}</div>}
          <Select
            key={`key_${JSON.stringify((valueData as any)?.value)}`}
            styles={customStyles}
            options={options}
            defaultValue={valueData}
            placeholder={placeholder}
            maxMenuHeight={200}
            classNamePrefix="sc"
            components={testId ? { DropdownIndicator: makeDropdownIndicator(testId) } : undefined}
            isDisabled={options.length === 0}
            formatOptionLabel={formatOptionLabel}
            isOptionDisabled={(option: OptionProps) => {
              if (option.value === (valueData as OptionProps)?.value) {
                return false;
              }
              return readOnly;
            }}
            onChange={(newValue: any) => {
              onChange(
                Array.isArray(newValue)
                  ? newValue.map((option: OptionProps) => option?.value)
                  : (newValue as OptionProps)?.value,
              );
              if (customOnChange)
                customOnChange(
                  Array.isArray(newValue)
                    ? newValue.map((option: OptionProps) => option?.value)
                    : (newValue as OptionProps)?.value,
                );
            }}
            {...selectProps}
          />
          {error && (
            <FormError
              className={styles.errorMessage}
              error={error.message}
            />
          )}
        </div>
      )}
    />
  );
};
